import { FirebaseAuthenticationClient } from '../firebase/auth/FirebaseAuthenticationClient';

type Claims = Awaited<ReturnType<typeof FirebaseAuthenticationClient.getClaims>>;

/**
 * クレイム取得が最終的に失敗した理由。呼び出し側の分岐に使う。
 *
 * - 'network': 通信起因。時間をおけば回復し得る
 * - 'auth': 認証情報そのものが失効している。リトライしても回復しない
 * - 'unknown': どちらとも判定できない。ログイン済みの状態を巻き戻す根拠にはしない
 */
export type ClaimsFailureKind = 'network' | 'auth' | 'unknown';

export class ClaimsFetchError extends Error {
  readonly kind: ClaimsFailureKind;
  readonly attempts: number;
  readonly originalError: unknown;

  constructor(kind: ClaimsFailureKind, attempts: number, originalError: unknown) {
    // Sentry のグルーピングが試行回数で分かれないよう、メッセージには attempts を含めない。
    super(`Failed to fetch custom claims (${kind})`);
    this.name = 'ClaimsFetchError';
    this.kind = kind;
    this.attempts = attempts;
    this.originalError = originalError;
  }
}

/**
 * 各リトライ前の待機時間(ミリ秒)の基準値。要素数がリトライ回数になる。
 * 実際の待機時間は {@link withJitter} でばらつかせる。
 */
const BaseRetryDelaysMilliSeconds = [500, 1000, 2000, 4000, 8000];

/**
 * 新しい試行を始めるのをやめる、開始からのデッドライン(ミリ秒)。
 * この間は画面がローディング表示のままになるため、待ちすぎて操作不能な時間が伸びないようにする。
 *
 * Firebase SDK のリクエストタイムアウトは1回あたり 30〜60 秒(DEFAULT_API_TIMEOUT_MS)あり、
 * 回線がハングすると1回の getClaims だけで数十秒かかる。待機の合計(最大 15.5 秒)だけを
 * 見ていても総時間は抑えられないため、壁時計で頭打ちにする。
 *
 * 即座に失敗する回線断では全リトライを使い切れる長さにしてある。逆にリクエストが
 * ハングする場合は 2 回程度で打ち切られるが、そこまで遅い回線ではリトライを重ねても
 * 回復しにくく、待たせ続けるほうが体験を損なう。
 */
const RetryDeadlineMilliSeconds = 60_000;

/**
 * リトライしても回復しない、認証そのものの失敗を表すエラーコード。
 *
 * user-token-expired / user-disabled は SDK 側が自動でサインアウトするが
 * (isUserInvalidated)、invalid-refresh-token はサインアウトされないため、
 * 呼び出し側が未ログインへ倒す必要がある。
 */
const NonRetryableAuthErrorCodes = new Set([
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-user-token',
  'auth/invalid-refresh-token',
  'auth/user-signed-out',
  'auth/requires-recent-login',
]);

/** 通信起因で、時間をおけば回復し得るエラーコード。 */
const NetworkErrorCodes = new Set(['auth/network-request-failed', 'auth/timeout', 'auth/internal-error']);

/**
 * Firebase Auth のレート制限。数秒のバックオフでは解除されないため再試行しない。
 * 一方で認証情報が失効したわけではないので、未ログインへ倒さず network と同じ扱いにする。
 */
const RateLimitErrorCode = 'auth/too-many-requests';

const errorCodeOf = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null || !('code' in error)) return null;
  const { code } = error as { code: unknown };
  return typeof code === 'string' ? code : null;
};

/**
 * 複数タブを開いている場合、各タブが同じ待機時間でリトライすると同じ瞬間に
 * トークンのリフレッシュ要求が集中する。待機時間の後半をランダム化してずらす。
 */
const withJitter = (milliSeconds: number): number => Math.round(milliSeconds / 2 + Math.random() * (milliSeconds / 2));

/**
 * バックオフの待機。回線が戻った直後に再試行できるよう、タイマーの満了と
 * online イベントのうち早い方で待機を打ち切る。
 */
const waitBeforeRetry = (milliSeconds: number): Promise<void> => {
  const controller = new AbortController();
  return new Promise<void>((resolve) => {
    const timerId = setTimeout(resolve, milliSeconds);
    controller.signal.addEventListener('abort', () => clearTimeout(timerId), { once: true });
    window.addEventListener('online', () => resolve(), { signal: controller.signal });
  }).finally(() => controller.abort());
};

type Options = {
  delaysMilliSeconds?: readonly number[];
  deadlineMilliSeconds?: number;
  wait?: (milliSeconds: number) => Promise<void>;
};

/**
 * カスタムクレイムの取得を、失敗時にリトライしながら行う。
 *
 * getClaims は ID トークンのリフレッシュを伴うため、スリープ復帰直後のような一時的な
 * ネットワーク断で失敗し得る。ここで諦めると呼び出し側が未ログイン状態へ倒れてしまうため、
 * 指数バックオフで回復を待つ。
 *
 * トークンが本当に失効している場合のエラーはリトライしても回復しないため、待たずに
 * 'auth' として送出する。Firebase SDK 側がサインアウトして onIdTokenChanged(null) を
 * 発火するため、呼び出し側はそちらで未ログインへ遷移できる。
 *
 * @param isStale より新しい認証状態の更新が始まっていれば true を返す関数
 */
export const getClaimsWithRetry = async (isStale: () => boolean, options: Options = {}): Promise<Claims> => {
  const {
    delaysMilliSeconds = BaseRetryDelaysMilliSeconds,
    deadlineMilliSeconds = RetryDeadlineMilliSeconds,
    wait = waitBeforeRetry,
  } = options;
  const deadline = Date.now() + deadlineMilliSeconds;

  const classify = (error: unknown): ClaimsFailureKind => {
    const code = errorCodeOf(error);
    if (code && NetworkErrorCodes.has(code)) return 'network';
    // コード不明のエラーでも、オフラインであれば通信起因とみなす。
    return navigator.onLine ? 'unknown' : 'network';
  };

  for (let attempt = 1; ; attempt++) {
    try {
      return await FirebaseAuthenticationClient.getClaims();
    } catch (error) {
      const code = errorCodeOf(error);
      if (code && NonRetryableAuthErrorCodes.has(code)) throw new ClaimsFetchError('auth', attempt, error);
      if (code === RateLimitErrorCode) throw new ClaimsFetchError('network', attempt, error);

      const remainingMilliSeconds = deadline - Date.now();
      if (isStale() || attempt > delaysMilliSeconds.length || remainingMilliSeconds <= 0) {
        throw new ClaimsFetchError(classify(error), attempt, error);
      }

      await wait(Math.min(withJitter(delaysMilliSeconds[attempt - 1]), remainingMilliSeconds));
      if (isStale() || Date.now() >= deadline) throw new ClaimsFetchError(classify(error), attempt, error);
    }
  }
};
